import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { t } from "@/lib/i18n";
import { toast } from "sonner";
import { Palette, Loader2, Sparkles, CheckCircle2, Type } from "lucide-react";

export function DesignerPanel() {
  const { lang } = useLang();
  const [ctx, setCtx] = useState("");
  const [briefs, setBriefs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  const load = async () => {
    const { data } = await api.get("/design/briefs?limit=20");
    setBriefs(data.briefs || []);
    const active = (data.briefs || []).find((b) => b.active);
    setPreview(active || (data.briefs || [])[0] || null);
  };

  useEffect(() => { load(); }, []);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/design/generate", { brand_context: ctx || null });
      setPreview(data);
      await load();
      toast.success(t(lang, "designer_generated"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const activate = async (bid) => {
    await api.post(`/design/briefs/${bid}/activate`);
    toast.success(t(lang, "designer_activated"));
    await load();
  };

  return (
    <div className="hq-card overflow-hidden" data-testid="designer-panel">
      <div className="p-5 border-b border-white/10 flex items-center gap-2 flex-wrap">
        <Palette size={14} className="text-[color:var(--hq-cyan)]" />
        <div className="font-display text-lg">{t(lang, "designer_title")}</div>
        <span className="ml-auto font-mono text-[10px] uppercase text-white/40">GPT-5.2 · brand system</span>
      </div>

      <div className="p-5 border-b border-white/10">
        <div className="font-mono uppercase text-[10px] text-white/40 mb-1">{t(lang, "designer_context")}</div>
        <textarea
          data-testid="designer-context-input"
          value={ctx}
          onChange={(e) => setCtx(e.target.value)}
          className="hq-input w-full h-24 text-xs"
          placeholder={t(lang, "designer_context_ph")}
        />
        <button
          data-testid="designer-generate-btn"
          onClick={generate}
          disabled={busy}
          className="hq-btn hq-btn-primary text-xs mt-2 disabled:opacity-50"
        >
          {busy ? <Loader2 size={10} className="animate-spin" /> : <Sparkles size={10} />}
          {busy ? t(lang, "designer_generating") : t(lang, "designer_generate")}
        </button>
      </div>

      {preview && (
        <div className="p-5 border-b border-white/10 space-y-4" data-testid="designer-preview">
          {/* Palette */}
          <div>
            <div className="font-mono uppercase text-[10px] text-white/40 mb-2">Palette</div>
            <div className="flex gap-2 flex-wrap">
              {["background", "foreground", "accent", "subtle"].map((k) => (
                preview.palette?.[k] && (
                  <div key={k} className="flex items-center gap-2 border border-white/10 p-2">
                    <div className="w-8 h-8 border border-white/20" style={{ background: preview.palette[k] }} />
                    <div>
                      <div className="text-[10px] font-mono uppercase text-white/50">{k}</div>
                      <div className="font-mono text-xs">{preview.palette[k]}</div>
                    </div>
                  </div>
                )
              ))}
            </div>
            {preview.palette?.rationale && (
              <div className="text-xs text-white/60 mt-2">{preview.palette.rationale}</div>
            )}
          </div>

          {/* Typography */}
          {preview.typography?.display && (
            <div className="flex items-center gap-2">
              <Type size={14} className="text-[color:var(--hq-cyan)]" />
              <div className="text-sm">{preview.typography.display} + {preview.typography.text}</div>
              <div className="text-white/50 text-xs ml-2">— {preview.typography.rationale}</div>
            </div>
          )}

          {/* Hero */}
          {(preview.hero?.headline?.[lang] || preview.hero?.headline?.en) && (
            <div className="border border-white/10 p-4">
              <div className="font-mono text-[10px] uppercase text-white/40 mb-2">Hero preview ({lang})</div>
              <div className="font-display text-2xl">
                {preview.hero.headline[lang] || preview.hero.headline.en}
              </div>
              <div className="text-white/70 text-sm mt-2">
                {preview.hero.subhead?.[lang] || preview.hero.subhead?.en}
              </div>
              <button className="hq-btn hq-btn-primary text-xs mt-3" disabled>
                {preview.hero.cta_label?.[lang] || preview.hero.cta_label?.en}
              </button>
            </div>
          )}

          {/* Trust badges */}
          {preview.trust_badges?.length > 0 && (
            <div>
              <div className="font-mono uppercase text-[10px] text-white/40 mb-2">Trust badges</div>
              <div className="flex flex-wrap gap-2">
                {preview.trust_badges.map((b, i) => (
                  <span key={i} className="text-xs px-2 py-1 border border-white/15">{b}</span>
                ))}
              </div>
            </div>
          )}

          {/* Sections */}
          {preview.sections?.length > 0 && (
            <div>
              <div className="font-mono uppercase text-[10px] text-white/40 mb-2">Sections</div>
              <ol className="text-xs text-white/70 list-decimal ml-5 space-y-1">
                {preview.sections.map((s, i) => (
                  <li key={i}><strong>{s.name}</strong> — {s.rationale}</li>
                ))}
              </ol>
            </div>
          )}
        </div>
      )}

      <div className="p-5">
        <div className="font-mono uppercase text-[10px] text-white/40 mb-3">{t(lang, "designer_briefs")}</div>
        {briefs.length === 0 ? (
          <div className="py-8 text-center font-mono text-xs text-white/40">{t(lang, "designer_none")}</div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {briefs.map((b) => (
              <div
                key={b.brief_id}
                data-testid={`designer-brief-${b.brief_id}`}
                className={`border rounded-lg overflow-hidden bg-white/[0.02] flex flex-col ${
                  b.active
                    ? "border-[color:var(--hq-success)]/50 shadow-[0_0_0_1px_var(--hq-success)]/30"
                    : "border-white/10 hover:border-white/25 transition-colors"
                }`}
              >
                {/* Palette strip */}
                <div className="h-16 relative overflow-hidden flex">
                  {["primary", "accent", "surface", "text"].map((k) => (
                    <div
                      key={k}
                      className="flex-1"
                      style={{ background: b.palette?.[k] || "#111" }}
                      title={`${k}: ${b.palette?.[k] || "n/a"}`}
                    />
                  ))}
                  {b.active && (
                    <div className="absolute top-2 right-2 inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-widest text-[color:var(--hq-success)] bg-black/60 backdrop-blur px-2 py-0.5 rounded-full border border-[color:var(--hq-success)]/40">
                      <CheckCircle2 size={9} /> {t(lang, "designer_active")}
                    </div>
                  )}
                </div>

                <div className="p-4 flex-1 flex flex-col gap-2">
                  <div className="text-sm text-white font-medium line-clamp-2 min-h-[2.5rem]">
                    {b.brand_voice || b.brief_id}
                  </div>
                  {b.typography?.display && (
                    <div className="font-mono text-[10px] uppercase tracking-widest text-white/50">
                      {b.typography.display} · {b.typography.text}
                    </div>
                  )}
                  <div className="font-mono text-[10px] text-white/30 mt-auto">
                    {new Date(b.generated_at).toLocaleString()}
                  </div>
                </div>

                <div className="p-3 border-t border-white/10 flex items-center gap-2 justify-end bg-black/20">
                  <button
                    onClick={() => setPreview(b)}
                    className="hq-btn hq-btn-ghost text-xs"
                    data-testid={`designer-preview-${b.brief_id}`}
                  >
                    {t(lang, "designer_view")}
                  </button>
                  {!b.active && (
                    <button
                      data-testid={`designer-activate-${b.brief_id}`}
                      onClick={() => activate(b.brief_id)}
                      className="hq-btn hq-btn-cyan text-xs"
                    >
                      {t(lang, "designer_activate")}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
